(() => {
  'use strict';
  if (window.MayhemSetupTools) return;
  const labelNode = document.getElementById('mayhem-setup-labels');
  if (!labelNode) return;
  const L = JSON.parse(labelNode.textContent);
  const toolbar = document.querySelector('.mayhem-setup-bar');
  if (toolbar) {
    const measureToolbar = () => document.body.style.setProperty('--mayhem-toolbar-height', `${Math.ceil(toolbar.getBoundingClientRect().height)}px`);
    new ResizeObserver(measureToolbar).observe(toolbar);
    measureToolbar();
  }
  const service = window.MayhemFitmentService;
  const dialog = document.getElementById('mayhem-setup-dialog');
  const body = dialog.querySelector('[data-setup-dialog-body]');
  const title = dialog.querySelector('h2');
  const requestedVariant = new URL(location.href).searchParams.get('variant');
  const productCache = new Map();
  const memory = new Map();
  let modalToken = 0;
  let alternativeToken = 0;
  let toastTimer;
  let setupVehicleRequest = null;
  let lastTrigger;
  let alternativesVisible = false;
  const track = (name, data = {}) => window.MayhemAnalytics?.track(name, data);
  const element = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const button = (label, action, className = '') => {
    const node = element('button', label, className); node.type = 'button';
    node.addEventListener('click', action); return node;
  };
  const link = (label, url) => { const node = element('a', label); node.href = url; return node; };
  const read = key => {
    if (memory.has(key)) return memory.get(key);
    try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value : []; }
    catch { return memory.get(key) || []; }
  };
  const persist = (key, value) => {
    memory.set(key, value);
    try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; }
  };
  const nativeVehicle = () => window.TWGFitmentPreview?.selectedVehicle();
  const cleanNative = value => {
    try {
      if(value?.query?.provider==='twg_app')return window.TWGFitmentModules.cleanVehicle(value.query,value.label);
      if (!value || !['baseVehicleId','submodelId','bodyTypeId','driveTypeId','regionId'].every(key=>/^\d{1,12}$/.test(String(value.query?.[key]||'')))) return null;
      return {query:Object.fromEntries(['baseVehicleId','submodelId','bodyTypeId','driveTypeId','regionId','customNote'].filter(key=>value.query[key]!==undefined).map(key=>[key,String(value.query[key])])),label:String(value.label||'Selected vehicle')};
    } catch {return null;}
  };
  const nativeKey = value => value ? 'fg:'+JSON.stringify(value.query) : '';
  const validRef = value => Boolean((!value?.fitmentVehicle || cleanNative(value.fitmentVehicle)) && value && /^[a-z0-9][a-z0-9-]{0,200}$/.test(value.handle || '') && /^\d{1,20}$/.test(String(value.variant || '')) && (!value.vehicle || /^\d{1,12}$/.test(String(value.vehicle))));
  const saved = () => read('mayhem:setups:v1').filter(validRef).slice(0, 12);
  const comparison = () => read('mayhem:compare:v1').filter(validRef).slice(0, 3);
  const vehicleId = () => {
    if(nativeVehicle())return '';
    for (const storage of ['localStorage', 'sessionStorage']) {
      try { const id = window[storage].getItem('mayhem:selected-vehicle-id'); if (/^\d{1,12}$/.test(id || '')) return id; } catch { /* Storage is optional. */ }
    }
    return '';
  };
  const cleanVisual = raw => {
    if (!raw || typeof raw !== 'object') return null;
    const out = {};
    if (/^\d{1,12}$/.test(String(raw.colorId || ''))) out.colorId = String(raw.colorId);
    if (out.colorId && typeof raw.colorName === 'string') out.colorName = raw.colorName.slice(0,100);
    if (raw.rideHeight != null && raw.rideHeight !== '' && Number.isFinite(Number(raw.rideHeight)) && Number(raw.rideHeight) >= -10 && Number(raw.rideHeight) <= 20) out.rideHeight = Number(raw.rideHeight);
    if (typeof raw.tireSize === 'string' && /^[A-Za-z0-9 .\/x×-]{1,40}$/.test(raw.tireSize)) out.tireSize = raw.tireSize;
    if (['winter','studio','landscape'].includes(raw.background)) out.background = raw.background;
    if ([1,14,32].includes(Number(raw.angle))) out.angle = Number(raw.angle);
    if (['oe', 'optional', 'plus'].includes(raw.fitmentType)) out.fitmentType = raw.fitmentType;
    if (Number(raw.fitmentDiameter) >= 10 && Number(raw.fitmentDiameter) <= 40) out.fitmentDiameter = Number(raw.fitmentDiameter);
    return Object.keys(out).length ? out : null;
  };
  const visualFields = ref => {
    const v = cleanVisual(ref.visual); if (!v) return [];
    return [[L.preview_color, v.colorName || v.colorId], [L.preview_height, v.rideHeight == null ? null : `${v.rideHeight > 0 ? '+' : ''}${v.rideHeight} in`], [L.preview_tire, v.tireSize], [L.preview_background, v.background], [L.preview_angle, ({1:'Side',14:'Rear',32:'Front'})[v.angle]]].filter(([,value]) => value != null && value !== '');
  };
  const visualParams = (url, ref) => {
    const v = cleanVisual(ref.visual); if (!v) return;
    for (const [key,field] of [['vehicle_color_id','colorId'],['vehicle_color_name','colorName'],['ride_height','rideHeight'],['tire_size','tireSize'],['background','background'],['view_angle','angle'],['fitment_type','fitmentType'],['fitment_diameter','fitmentDiameter']]) if (v[field] != null) url.searchParams.set(key, v[field]);
  };
  const visualizerUrl = setup => {
    const url = new URL('/pages/visualizer', location.origin);
    url.searchParams.set('vehicle_id', setup.ref.vehicle);
    url.searchParams.set('wheel_pn', setup.variant.sku);
    visualParams(url, setup.ref);
    return preview(url).href;
  };
  const refKey = ref => [ref.handle, ref.variant, nativeKey(cleanNative(ref.fitmentVehicle)) || ref.vehicle || '', JSON.stringify(cleanVisual(ref.visual))].join(':');
  const compareKey = ref => [ref.handle, ref.variant].join(':');
  const counters = () => {
    const garageCount = garage().length;
    document.querySelectorAll('[data-setup-garage-count]').forEach(node => {
      node.textContent = String(garageCount);
      node.hidden = garageCount === 0;
    });
    document.querySelectorAll('[data-setup-saved-count]').forEach(node => { node.textContent = String(saved().length); node.closest('button')?.classList.toggle('mayhem-setup-has-items', saved().length > 0); });
    document.querySelectorAll('[data-setup-compare-count]').forEach(node => { node.textContent = String(comparison().length); node.closest('button')?.classList.toggle('mayhem-setup-has-items', comparison().length > 0); });
  };
  const announce = (message, action) => {
    const node = document.querySelector('[data-setup-toast]');
    if (!node) return;
    clearTimeout(toastTimer);
    if (dialog.open) dialog.append(node);
    else document.body.append(node);
    node.replaceChildren(element('span', message));
    node.hidden = false;
    if (action) {
      const target = document.querySelector('.mayhem-setup-bar [data-setup-action="' + action + '"]');
      if (target && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        target.animate([{boxShadow:'0 0 0 0 #a6262366'},{boxShadow:'0 0 0 8px #a6262300'}], {duration:650,iterations:2});
      }
      const view = button(action === 'saved' ? L.view_saved_setups : L.view_comparison, () => {
        node.hidden = true;
        lastTrigger = target && target.getClientRects().length ? target : document.querySelector('#header-component button');
        if (action === 'saved') showSaved(); else showCompare();
      }, 'mayhem-setup-toast__action');
      node.append(view);
      // Keep actionable feedback available until used or dismissed.
      node.append(button(L.close, () => { node.hidden = true; }, 'mayhem-setup-toast__dismiss'));
    } else {
      toastTimer = setTimeout(() => { if (!node.contains(document.activeElement)) node.hidden = true; }, 5000);
    }
  };
  const preview = url => {
    const id = new URL(location.href).searchParams.get('preview_theme_id');
    if (/^\d+$/.test(id || '')) url.searchParams.set('preview_theme_id', id);
    return url;
  };
  const setupUrl = (ref, path) => {
    const url = new URL(path || `/products/${ref.handle}`, location.origin);
    url.searchParams.set('variant', ref.variant);
    url.searchParams.set('setup', '1');
    if (path) url.searchParams.set('setup_handle', ref.handle);
    if (ref.fitmentVehicle) url.searchParams.set('setup_fg',JSON.stringify(cleanNative(ref.fitmentVehicle)));
    else if (ref.vehicle) url.searchParams.set('setup_vehicle', ref.vehicle);
    visualParams(url, ref);
    return preview(url).href;
  };
  const productUrl = ref => {
    const url = new URL(`/products/${ref.handle}`, location.origin);
    url.searchParams.set('variant', ref.variant); return preview(url).href;
  };
  const parseData = (text, selector) => {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const node = doc.querySelector(selector); if (!node) throw new Error('Missing setup data');
    return JSON.parse(node.textContent);
  };
  const fetchSection = async url => {
    const response = await fetch(preview(url), { signal: AbortSignal.timeout(15000) });
    if (!response.ok) { const error = new Error('Setup request failed'); error.unavailable = response.status === 404 || response.status === 410; throw error; }
    return response.text();
  };
  document.querySelectorAll('[data-mayhem-setup-product]').forEach(node => {
    try { const product = JSON.parse(node.textContent); if (product.handle) productCache.set(product.handle, Promise.resolve(product)); } catch { /* No product on this page. */ }
  });
  const productFor = handle => {
    if (!/^[a-z0-9][a-z0-9-]{0,200}$/.test(handle || '')) return Promise.reject(new Error('Invalid product'));
    if (!productCache.has(handle)) {
      const url = new URL(`/products/${handle}`, location.origin); url.searchParams.set('section_id', 'mayhem-setup-data');
      productCache.set(handle, fetchSection(url).then(html => parseData(html, '[data-mayhem-setup-product]')).then(product => {
        if (product.handle !== handle || !Array.isArray(product.variants)) throw new Error('Unavailable product');
        return product;
      }).catch(error => { productCache.delete(handle); throw error; }));
    }
    return productCache.get(handle);
  };
  const loadSetup = async ref => {
    if (!validRef(ref)) throw new Error('Invalid setup');
    const product = await productFor(ref.handle);
    const variant = product.variants.find(row => String(row.id) === String(ref.variant));
    if (!variant?.sku) { const error = new Error('Configuration no longer available'); error.unavailable = true; throw error; }
    const setup = { ref, product, variant, vehicle: null, result: { state: 'unknown', reasons: [L.check_vehicle] } };
    if (ref.fitmentVehicle) {
      setup.fitmentVehicle=cleanNative(ref.fitmentVehicle);
      try {setup.result=await window.TWGFitmentPreview.evaluateVehicle(setup.fitmentVehicle,variant.sku);}
      catch {setup.result={state:'unknown',reasons:[L.fitment_error]};}
    } else if (ref.vehicle) {
      try {
        const [vehicle, wheels] = await Promise.all([service.vehicle(ref.vehicle), service.wheels([variant.sku])]);
        setup.vehicle = vehicle;
        setup.result = service.evaluate(vehicle, wheels.get(variant.sku.toUpperCase()));
      } catch { setup.result = { state: 'unknown', reasons: [L.fitment_error] }; }
    }
    return setup;
  };
  const vehicleName = setup => setup.fitmentVehicle ? setup.fitmentVehicle.label : setup.vehicle ? [setup.vehicle.Year, setup.vehicle.Make, setup.vehicle.Model, setup.vehicle.Submodel, setup.vehicle.Body, setup.vehicle.Bed].filter(Boolean).join(' · ') : setup.ref.vehicle ? L.vehicle_unavailable : L.no_vehicle;
  const fields = ['sku', 'finish', 'size', 'bolt', 'offset', 'bore', 'load', 'backspace', 'lug_seat', 'weight'];
  const fieldValue = (setup, field) => {
    const value = field === 'finish' ? setup.product.finish : setup.variant[field];
    if (value == null || value === '' || field === 'weight' && Number(value) <= 0) return L.unavailable;
    const units = { bore: ' mm', load: ' lb', backspace: ' in' };
    if (field === 'weight') return `${Number(value)} lb`;
    if (field === 'offset') return /mm/i.test(String(value)) ? String(value) : `${Number(value) > 0 ? '+' : ''}${value} mm`;
    return String(value) + (units[field] || '');
  };
  const summaryText = setup => [setup.product.title, `${L.vehicle}: ${vehicleName(setup)}`, ...fields.map(field => `${L[field]}: ${fieldValue(setup, field)}`), ...visualFields(setup.ref).map(([label,value]) => `${label}: ${value}`), ...(setup.ref.visual ? [L.preview_note] : []), `${L.fitment}: ${window.MayhemFitmentUI.labels[setup.result.state]}`, window.MayhemFitmentUI.guidance(setup.result.state), ...setup.result.reasons, L.installation_note, setupUrl(setup.ref)].join('\n');
  const positionMenu = () => {
    if (!dialog.classList.contains('mayhem-setup-menu')) return;
    const mobile = matchMedia('(max-width:749px)').matches;
    dialog.classList.toggle('mayhem-setup-sheet', mobile);
    if (mobile) {
      for (const name of ['left', 'top', 'max-height']) dialog.style.removeProperty(name);
      return;
    }
    const anchor = lastTrigger?.isConnected && lastTrigger.getClientRects().length ? lastTrigger :
      [...document.querySelectorAll('[data-setup-action="compare"], [data-setup-action="garage"], [data-setup-action="saved"]')].find(node => node.getClientRects().length);
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(420, innerWidth - 24);
    const top = Math.max(12, Math.min(rect.bottom + 8, innerHeight - 180));
    dialog.style.left = Math.max(12, Math.min(rect.left, innerWidth - width - 12)) + 'px';
    dialog.style.top = top + 'px';
    dialog.style.maxHeight = Math.max(160, innerHeight - top - 12) + 'px';
  };
  const present = () => {
    const menu = dialog.classList.contains('mayhem-setup-menu');
    const modal = !menu || matchMedia('(max-width:749px)').matches;
    if (dialog.open && dialog.matches(':modal') !== modal) dialog.close();
    positionMenu();
    if (!dialog.open) { if (modal) dialog.showModal(); else dialog.show(); }
  };
  const open = (heading, menu = false) => {
    const token = ++modalToken;
    if (!dialog.open && !lastTrigger?.isConnected) lastTrigger = document.activeElement;
    const feedback = document.querySelector('[data-setup-toast]');
    if (feedback) feedback.hidden = true;
    title.textContent = heading; body.replaceChildren(element('p', L.loading));
    dialog.classList.toggle('mayhem-setup-menu', menu);
    if (!menu) {
      dialog.classList.remove('mayhem-setup-sheet');
      for (const name of ['left', 'top', 'max-height']) dialog.style.removeProperty(name);
    }
    present();
    lastTrigger?.setAttribute('aria-expanded', 'true');
    return token;
  };
  const showError = (token, retry, error) => {
    if (token !== modalToken) return;
    body.replaceChildren(element('p', error?.unavailable ? L.configuration_unavailable : L.error));
    const actions = element('div', null, 'mayhem-setup-actions');
    if (!error?.unavailable) actions.append(button(L.retry, retry));
    actions.append(link(L.browse_wheels, preview(new URL('/collections/all-wheels', location.origin)).href), link(L.support, preview(new URL('/pages/contact', location.origin)).href));
    body.append(actions);
  };
  const imageFor = setup => {
    if (!setup.variant.image) return null;
    const img = element('img'); img.src = setup.variant.image; img.alt = setup.product.title; img.width = 360; img.height = 360; img.loading = 'lazy'; return img;
  };
  const paintFitment = (node, setup) => {
    window.MayhemFitmentUI.paint(node, setup.result.state, { scope: 'sku', sku: setup.variant.sku, configuration: window.MayhemFitmentUI.configuration(setup.variant), vehicle: vehicleName(setup), reasons: setup.result.reasons });
    if(setup.result.label && node.querySelector('summary'))node.querySelector('summary').textContent=setup.result.label;
  };
  const pickSetupVehicle = (ref, event) => {
    event?.stopPropagation();
    const anchor = [...document.querySelectorAll('[data-mayhem-mobile-vehicle-select], [data-mayhem-mobile-vehicle-change], [data-setup-action="garage"]')].find(node => node.getClientRects().length);
    dialog.close();
    setupVehicleRequest = ref || null;
    if (!window.mayhemOpenVehiclePicker?.(anchor || lastTrigger)) {
      setupVehicleRequest = null;
      announce(L.vehicle_unavailable);
      return;
    }
    const surface = document.querySelector('[data-mayhem-collection-vehicle-dialog]');
    surface?.addEventListener('close', () => { setupVehicleRequest = null; }, { once: true });
  };
  const useVehicle = async (setup, event) => {
    if(setup.fitmentVehicle){window.TWGFitmentPreview.selectVehicle(setup.fitmentVehicle.query,setup.fitmentVehicle.label,{analyticsSource:'saved_garage'});announce(L.vehicle_applied);return;}
    if (!setup.vehicle) { pickSetupVehicle(setup.ref, event); return; }
    const row = setup.vehicle;
    const vehicle = { id: String(row.Id), year: row.Year, make: row.Make, model: row.Model, submodel: row.Submodel, body: row.Body, bed: row.Bed };
    let stored = false;
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].setItem('mayhem:selected-vehicle-id', vehicle.id); window[name].setItem('mayhem:selected-vehicle', JSON.stringify(vehicle)); stored = true; } catch { /* Try the other storage area. */ }
    }
    if (!stored) { announce(L.save_failed); return; }
    window.dispatchEvent(new CustomEvent('mayhem:vehicle-changed', { detail: { id: vehicle.id, vehicle, source: 'saved-setup' } }));
    announce(L.vehicle_applied);
  };
  const saveSetup = ref => {
    if (!validRef(ref)) { announce(L.error); return; }
    const values = saved();
    if (!values.some(item => refKey(item) === refKey(ref))) {
      if (values.length >= 12) { announce(L.save_limit); return; }
      values.unshift({ handle: ref.handle, variant: String(ref.variant), vehicle: ref.vehicle || '', ...(ref.fitmentVehicle?{fitmentVehicle:cleanNative(ref.fitmentVehicle)}:{}), ...(cleanVisual(ref.visual) ? { visual: cleanVisual(ref.visual) } : {}) });
    }
    const persisted = persist('mayhem:setups:v1', values); counters();
    announce(persisted ? L.save_success : L.save_failed, persisted ? 'saved' : null); track('mayhem_setup_saved', { variant_id: ref.variant });
  };
  const addCompare = ref => {
    const values = comparison();
    if (values.some(item => compareKey(item) === compareKey(ref))) { showCompare(); return; }
    if (values.length >= 3) { announce(L.compare_limit); showCompare(); return; }
    values.push({ handle: ref.handle, variant: String(ref.variant) });
    const persisted = persist('mayhem:compare:v1', values); counters(); announce(persisted ? L.compare_added : L.save_failed, persisted ? 'compare' : null);
    track('mayhem_compare_added', { variant_id: ref.variant });
  };
  const shareSetup = async ref => {
    const url = setupUrl(ref);
    const token = modalToken;
    let node = body.querySelector('.mayhem-setup-link');
    if (!node) {
      node = element('div', null, 'mayhem-setup-link');
      const label = element('label', L.link);
      const input = element('input');
      input.type = 'url'; input.readOnly = true; input.value = url;
      label.append(input); node.append(label); body.append(node);
    }
    const input = node.querySelector('input'); input.value = url;
    announce(L.link);
    node.scrollIntoView({ block: 'nearest' });
    try {
      await navigator.clipboard.writeText(url);
      if (token !== modalToken) return;
      announce(L.copied); track('mayhem_setup_shared', { variant_id: ref.variant });
    } catch {
      if (token !== modalToken) return;
      announce(L.copy_failed);
      input.focus(); input.select();
    }
    node.scrollIntoView({ block: 'nearest' });
  };
  const setupCard = (setup, { actions = true } = {}) => {
    const card = element('article', null, 'mayhem-setup-detail');
    const image = imageFor(setup); if (image) card.append(image);
    const content = element('div'); content.append(element('h3', setup.product.title), element('p', vehicleName(setup), 'mayhem-setup-vehicle'));
    const specs = element('dl', null, 'mayhem-setup-specs');
    fields.forEach(field => { specs.append(element('dt', L[field]), element('dd', fieldValue(setup, field))); });
    content.append(specs);
    const appearance = visualFields(setup.ref);
    if (appearance.length) {
      content.append(element('h4', L.preview_options));
      const visualSpecs = element('dl', null, 'mayhem-setup-specs');
      appearance.forEach(([label,value]) => visualSpecs.append(element('dt', label), element('dd', value)));
      content.append(visualSpecs, element('p', L.preview_note, 'mayhem-setup-small'));
    }
    const fitment = element('div'); paintFitment(fitment, setup); content.append(fitment, element('p', L.rechecked, 'mayhem-setup-small'), element('p', L.installation_note, 'mayhem-setup-small'));
    if (actions) {
      const controls = element('div', null, 'mayhem-setup-actions');
      controls.append(button(L.save, () => saveSetup(setup.ref)), button(L.share, () => shareSetup(setup.ref)), button(L.print, () => { track('mayhem_setup_printed', { variant_id: setup.ref.variant }); window.print(); }), button(L.compare, () => addCompare(setup.ref)));
      const dealer = link(L.dealer, setupUrl(setup.ref, '/pages/dealers')); dealer.dataset.setupDealer = 'true';
      if (setup.ref.vehicle && !setup.fitmentVehicle) controls.append(link(L.open_visualizer, visualizerUrl(setup)));
      if (setup.fitmentVehicle) controls.append(button(L.use_vehicle,()=>useVehicle(setup)));
      if (setup.fitmentVehicle?.query.provider==='twg_app' && ['fits','caution'].includes(setup.result.state)) controls.append(button('View on vehicle',async()=>{await useVehicle(setup);dialog.close();window.TWGOpenVisualizer?.(setup.variant.sku,undefined,id=>{location.assign(visualizerUrl({...setup,ref:{...setup.ref,vehicle:id}}));});}));
      controls.append(dealer, link(L.support, setupUrl(setup.ref, '/pages/contact')), link(L.product, productUrl(setup.ref)));
      if (setup.vehicle && String(setup.vehicle.Id) !== vehicleId()) controls.append(button(L.use_vehicle, () => useVehicle(setup)));
      if (!setup.ref.vehicle && !setup.fitmentVehicle) controls.append(button(L.select_vehicle, event => useVehicle(setup, event)));
      content.append(controls);
    }
    card.append(content); return card;
  };
  const showSetup = async (ref, sharing = false) => {
    const token = open(L.setup_title);
    try {
      const setup = await loadSetup(ref); if (token !== modalToken) return;
      body.replaceChildren(setupCard(setup));
      if (sharing) await shareSetup(ref);
    } catch (error) { showError(token, () => showSetup(ref, sharing), error); }
  };
  const showSaved = async (forVehicle = null) => {
    const token = open(L.saved_setups, true); const refs = saved().filter(ref => !forVehicle || (nativeKey(cleanNative(ref.fitmentVehicle)) || ref.vehicle) === forVehicle);
    body.replaceChildren(element('p', L.saved_notice, 'mayhem-setup-small'));
    if (!refs.length) { body.append(element('p', L.empty_saved)); return; }
    const list = element('div', null, 'mayhem-saved-list'); body.append(list);
    for (const ref of refs) {
      const item = element('article'); list.append(item);
      const remove = button(L.remove, () => { persist('mayhem:setups:v1', saved().filter(value => refKey(value) !== refKey(ref))); counters(); showSaved(forVehicle); });
      try {
        const product = await productFor(ref.handle); if (token !== modalToken) return;
        const variant = product.variants.find(row => String(row.id) === String(ref.variant));
        if (!variant) throw new Error('Removed variant');
        item.classList.add('mayhem-saved-summary');
        const image = imageFor({ product, variant }); if (image) item.append(image);
        const summary = element('div');
        summary.append(element('h3', product.title), element('p', [variant.sku, window.MayhemFitmentUI.configuration(variant)].join(' · ')));
        const vehicleLine = element('p', ref.fitmentVehicle ? ref.fitmentVehicle.label : ref.vehicle ? L.loading : L.no_vehicle, 'mayhem-setup-small');
        summary.append(vehicleLine);
        if (ref.vehicle) service.vehicle(ref.vehicle).then(vehicle => {
          if (token === modalToken) vehicleLine.textContent = vehicleName({ ref, vehicle });
        }).catch(() => { if (token === modalToken) vehicleLine.textContent = L.vehicle_unavailable; });
        const actions = element('div', null, 'mayhem-setup-actions');
        actions.append(button(L.view_setup, () => showSetup(ref)), remove);
        summary.append(actions); item.append(summary);
      } catch { if (token !== modalToken) return; item.append(element('p', L.error), remove); }
    }
  };
  const showCompare = async () => {
    const token = open(L.compare, true);
    const refs = comparison();
    body.replaceChildren();
    if (!refs.length) { body.append(element('p', L.empty_compare)); return; }
    const list = element('div', null, 'mayhem-saved-list');
    body.append(list);
    const actions = element('div', null, 'mayhem-setup-actions');
    actions.append(button(L.view_comparison, showComparisonTable));
    body.append(actions);
    for (const ref of refs) {
      const item = element('article');
      list.append(item);
      const remove = button(L.remove, () => {
        persist('mayhem:compare:v1', comparison().filter(value => compareKey(value) !== compareKey(ref)));
        counters(); showCompare();
      });
      try {
        const product = await productFor(ref.handle);
        if (token !== modalToken) return;
        const variant = product.variants.find(row => String(row.id) === String(ref.variant));
        if (!variant) throw new Error('Removed variant');
        item.classList.add('mayhem-saved-summary');
        const image = imageFor({ product, variant }); if (image) item.append(image);
        const summary = element('div');
        summary.append(element('h3', product.title), element('p', [variant.sku, window.MayhemFitmentUI.configuration(variant)].join(' · ')));
        const controls = element('div', null, 'mayhem-setup-actions');
        controls.append(remove); summary.append(controls); item.append(summary);
      } catch { if (token !== modalToken) return; item.append(element('p', L.configuration_unavailable), remove); }
    }
  };
  const showComparisonTable = async () => {

    const token = open(L.compare); const refs = comparison(); const id = vehicleId();
    if (!refs.length) { body.replaceChildren(element('p', L.empty_compare)); return; }
    try {
      const results = await Promise.allSettled(refs.map(ref => loadSetup({ ...ref, vehicle: id, fitmentVehicle:nativeVehicle() })));
      if (token !== modalToken) return;
      body.replaceChildren(element('p', L.compare_note, 'mayhem-setup-small'));
      const firstSetup = results.find(result => result.status === 'fulfilled')?.value;
      body.append(element('p', firstSetup ? vehicleName(firstSetup) : L.no_vehicle));
      if (!id && !nativeVehicle()) body.append(button(L.select_vehicle, event => pickSetupVehicle(null, event)));
      const toggleLabel = element('label', L.differences, 'mayhem-compare-toggle'); const toggle = element('input'); toggle.type = 'checkbox'; toggle.checked = true; toggleLabel.prepend(toggle); body.append(toggleLabel);
      const scroller = element('div', null, 'mayhem-compare-scroll'); scroller.tabIndex = 0; scroller.setAttribute('role', 'region'); scroller.setAttribute('aria-label', L.compare);
      const table = element('table', null, 'mayhem-compare-table'); table.style.setProperty('--compare-columns', refs.length); table.classList.add('show-differences');
      const head = element('thead'); const header = element('tr'); header.append(element('th', L.compare));
      results.forEach((result, index) => {
        const th = element('th'); th.scope = 'col';
        if (result.status === 'fulfilled') {
          const setup = result.value; const img = imageFor(setup); if (img) th.append(img);
          th.append(link(setup.product.title, productUrl(setup.ref)), element('p', setup.variant.sku));
        } else th.append(element('p', L.error));
        th.append(button(L.remove, () => { persist('mayhem:compare:v1', comparison().filter(value => compareKey(value) !== compareKey(refs[index]))); counters(); showComparisonTable(); }));
        header.append(th);
      });
      head.append(header); table.append(head); const tbody = element('tbody');
      [...fields, 'fitment'].forEach(field => {
        const row = element('tr'); const heading = element('th', L[field]); heading.scope = 'row'; row.append(heading);
        const values = results.map(result => result.status === 'fulfilled' ? field === 'fitment' ? result.value.result.state : fieldValue(result.value, field) : L.unavailable);
        if (new Set(values).size > 1) row.classList.add('is-different');
        results.forEach((result, index) => { const cell = element('td'); if (field === 'fitment' && result.status === 'fulfilled') paintFitment(cell, result.value); else cell.textContent = values[index]; row.append(cell); });
        tbody.append(row);
      });
      table.append(tbody); scroller.append(table); body.append(scroller);
      toggle.addEventListener('change', () => table.classList.toggle('show-differences', toggle.checked));
      track('mayhem_compare_opened', { configuration_count: refs.length, vehicle_id: id });
    } catch { showError(token, showComparisonTable); }
  };
  const currentRef = () => {
    const root = document.querySelector('[data-mayhem-pdp-summary]'); const specs = document.querySelector('[data-mayhem-specs]');
    if (!root || !specs || specs.dataset.fitmentPending === 'true' || !specs.dataset.selectedSku || !specs.dataset.selectedVariantId) return null;
    const ref = { handle: root.dataset.productHandle, variant: specs.dataset.selectedVariantId, vehicle: vehicleId(), ...(nativeVehicle()?{fitmentVehicle:nativeVehicle()}:{}) };
    return validRef(ref) ? ref : null;
  };
  const syncCurrent = () => {
    const ref = currentRef();
    document.querySelectorAll('[data-setup-pdp] button').forEach(node => { node.disabled = !ref; });
    document.querySelectorAll('[data-mayhem-pdp-summary] a[href*="/pages/dealers"]').forEach(node => {
      const partial = new URL('/pages/dealers', location.origin);
      const product = document.querySelector('[data-mayhem-pdp-summary]');
      if (product) partial.searchParams.set('wheel_preferences', product.dataset.productHandle);
      node.href = ref ? setupUrl(ref, '/pages/dealers') : preview(partial).href; node.dataset.setupDealer = 'true';
    });
  };
  const chooseCard = card => {
    const handle = card.dataset.handle; let candidates;
    const badge = card.querySelector('[data-mayhem-card-fitment]');
    try {
      candidates = (nativeVehicle() || vehicleId()) && badge?.dataset.matchedVariants ? JSON.parse(badge.dataset.matchedVariants) : JSON.parse(card.querySelector('[data-mayhem-card-variants]').textContent);
    } catch { announce(L.error); return; }
    const tools = document.querySelector('[data-mayhem-collection-tools]');
    if (tools?.dataset.fitmentChecking === 'true') { announce(L.loading); return; }
    if (tools?.dataset.directFitOnly === 'true') candidates = candidates.filter(row => row.state === 'fits');
    if (!candidates?.length) { announce(L.select_configuration); return; }
    if (candidates.length === 1) { addCompare({ handle, variant: String(candidates[0].id) }); return; }
    open(L.compare_choose); body.replaceChildren(element('p', L.compare_choose_note));
    const list = element('div', null, 'mayhem-configuration-choices');
    candidates.forEach(variant => {
      list.append(button(`${variant.sku} · ${variant.size} · ${variant.bolt} · ${variant.offset}`, () => { addCompare({ handle, variant: String(variant.id) }); if (comparison().some(ref => String(ref.variant) === String(variant.id))) dialog.close(); }));
    });
    body.append(list);
  };
  const refFromUrl = () => {
    const url = new URL(location.href); if (url.searchParams.get('setup') !== '1') return null;
    const ref = { handle: url.searchParams.get('setup_handle') || location.pathname.match(/\/products\/([^/]+)/)?.[1], variant: url.searchParams.get('variant'), vehicle: url.searchParams.get('setup_vehicle') || '' };
    if(url.searchParams.has('setup_fg')) {try {ref.fitmentVehicle=cleanNative(JSON.parse(url.searchParams.get('setup_fg')));if(!ref.fitmentVehicle)return null;}catch{return null;}}
    ref.visual = cleanVisual({ colorId: url.searchParams.get('vehicle_color_id'), colorName: url.searchParams.get('vehicle_color_name'), rideHeight: url.searchParams.get('ride_height'), tireSize: url.searchParams.get('tire_size'), background: url.searchParams.get('background'), angle: url.searchParams.get('view_angle'), fitmentType: url.searchParams.get('fitment_type'), fitmentDiameter: url.searchParams.get('fitment_diameter') });
    return validRef(ref) ? ref : null;
  };
  const handoffQuestion = ref => {
    try { return sessionStorage.getItem('mayhem:dealer-question:' + refKey(ref)) || ''; } catch { return ''; }
  };
  const initHandoff = async () => {
    const hosts = [...document.querySelectorAll('[data-setup-handoff]')]; const ref = refFromUrl();
    if (!hosts.length) return;
    if (!ref) {
      const handle = new URL(location.href).searchParams.get('wheel_preferences');
      if (handle) {
        try {
          const info = JSON.parse(sessionStorage.getItem('mayhem:configuration-preferences') || 'null');
          if (info?.handle === handle && /^[a-z0-9-]+$/.test(handle)) hosts.forEach(host => {
            host.hidden = false;
            host.replaceChildren(element('h2', info.title), element('p', info.selection), element('p', info.help), link(info.back, preview(new URL('/products/' + handle, location.origin)).href));
          });
        } catch {}
      }
      return;
    }
    hosts.forEach(host => { host.hidden = false; host.textContent = L.loading; });
    try {
      const setup = await loadSetup(ref);
      hosts.forEach(host => {
        const contact = host.dataset.setupHandoff === 'contact';
        host.replaceChildren(element('h2', contact ? L.setup_title : L.handoff_title), element('p', contact ? L.handoff_contact : L.handoff_dealer), element('p', [setup.product.title, setup.variant.sku, vehicleName(setup)].join(' · ')), button(L.view_setup, () => showSetup(ref)));
        if (!contact) {
          const fitment = element('div'); paintFitment(fitment, setup);
          host.append(fitment);
          if (ref.vehicle || ref.fitmentVehicle) {
            host.append(element('p', `${L.dealer_next}: ${window.MayhemFitmentUI.guidance(setup.result.state)}`, 'mayhem-setup-small'));
            if (['nofit','unknown'].includes(setup.result.state)) {
              const alternatives = new URL('/collections/all-wheels', location.origin);
              if (ref.vehicle) alternatives.searchParams.set('vehicle_id', ref.vehicle);
              const preview = new URL(location.href).searchParams.get('preview_theme_id');
              if (preview) alternatives.searchParams.set('preview_theme_id', preview);
              host.append(link(L.dealer_alternatives, alternatives.href));
            }
          } else host.append(element('p', L.check_vehicle));
        }
        const appearance = visualFields(ref);
        if (appearance.length) host.append(element('p', appearance.map(([label,value]) => `${label}: ${value}`).join(' · ')), element('p', L.preview_note, 'mayhem-setup-small'));
        if (!contact) {
          const key = 'mayhem:dealer-question:' + refKey(ref);
          const questionLabel = element('label', L.dealer_question, 'mayhem-dealer-question');
          const question = element('textarea'); question.rows = 2; question.maxLength = 1000;
          question.value = handoffQuestion(ref); questionLabel.append(question, element('span', L.dealer_question_help, 'mayhem-setup-small'));
          const questionDetails = element('details', null, 'mayhem-dealer-review');
          questionDetails.append(element('summary', L.dealer_question), questionLabel);
          const review = element('details', null, 'mayhem-dealer-review');
          review.append(element('summary', L.dealer_review));
          const message = element('textarea'); message.readOnly = true; message.rows = 6;
          message.setAttribute('aria-label', L.dealer_review); review.append(message);
          const refresh = () => {
            message.value = [summaryText(setup), question.value.trim() ? `${L.dealer_question}: ${question.value.trim()}` : ''].filter(Boolean).join('\n\n');
            try { sessionStorage.setItem(key, question.value.slice(0,1000)); } catch {}
          };
          question.addEventListener('input', refresh); refresh();
          const actions = element('div', null, 'mayhem-setup-actions');
          actions.append(button(L.dealer_copy, async () => {
            try { await navigator.clipboard.writeText(message.value); announce(L.dealer_copied); track('mayhem_dealer_details_copied', { variant_id: ref.variant }); }
            catch { review.open = true; message.focus(); message.select(); announce(L.dealer_copy_failed); }
          }));
          if (navigator.share) actions.append(button(L.share, async () => {
            try { await navigator.share({ title: setup.product.title, text: message.value, url: setupUrl(ref) }); track('mayhem_setup_shared', { variant_id: ref.variant }); }
            catch (error) { if (error.name !== 'AbortError') { review.open = true; message.focus(); message.select(); announce(L.dealer_copy_failed); } }
          }));
          actions.append(link(L.support, setupUrl(ref, '/pages/contact')));
          const disclosures = element('div', null, 'mayhem-dealer-disclosures');
          disclosures.append(questionDetails, review);
          host.append(actions, disclosures, element('p', L.dealer_privacy, 'mayhem-setup-small'));
        }
        if (contact) {
          window.dispatchEvent(new CustomEvent('mayhem:contact-context', { detail: { created: Date.now(), vehicle: vehicleName(setup), wheel: `${setup.product.title} · ${setup.variant.sku} · ${window.MayhemFitmentUI.configuration(setup.variant)}`, question: handoffQuestion(ref), message: summaryText(setup) } }));
        }
      });
    } catch {
      hosts.forEach(host => host.replaceChildren(element('p', L.dealer_load_error), button(L.retry, initHandoff), link(L.browse_wheels, '/collections/all-wheels'), link(L.support, setupUrl(ref, '/pages/contact'))));
    }
  };
  let catalogPromise;
  const catalog = () => {
    if (!catalogPromise) catalogPromise = (async () => {
      const products = []; let next = '/collections/all-wheels'; const visited = new Set();
      while (next) {
        const url = new URL(next, location.origin);
        if (url.origin !== location.origin || visited.has(url.href) || visited.size >= 20) throw new Error('Invalid catalog pagination');
        visited.add(url.href); url.searchParams.set('section_id', 'mayhem-setup-catalog');
        const data = parseData(await fetchSection(url), '[data-mayhem-setup-catalog]');
        products.push(...data.products); next = data.next;
      }
      products.forEach(product => productCache.set(product.handle, Promise.resolve(product))); return products;
    })().catch(error => { catalogPromise = null; throw error; });
    return catalogPromise;
  };
  const alternatives = async () => {
    const host = document.querySelector('[data-setup-alternatives-body]'); if (!host || !alternativesVisible) return;
    const token = ++alternativeToken; const native=nativeVehicle();const id = nativeKey(native) || vehicleId();
    const browse = document.querySelector('[data-alternatives-browse]');
    if (browse) browse.href = preview(new URL(browse.getAttribute('href'), location.origin)).href;
    if (!id) { host.replaceChildren(); return; }
    host.replaceChildren(element('p', L.loading));
    try {
      const [products, vehicle] = await Promise.all([catalog(), native?Promise.resolve({}):service.vehicle(id)]);
      if (token !== alternativeToken) return;
      if (!vehicle) throw new Error('Vehicle unavailable');
      const current = document.querySelector('[data-mayhem-pdp-summary]')?.dataset.productHandle;
      const currentStyle = products.find(product => product.handle === current)?.style;
      const choices = products.filter(product => product.handle !== current && (!currentStyle || String(product.style) !== String(currentStyle))).flatMap(product => product.variants.filter(variant => {
        if(native)return true;
        const bolts = [...String(variant.bolt || '').matchAll(/(\d+)[-xX](\d+(?:\.\d+)?)/g)];
        return bolts.some(bolt => Number(bolt[1]) === Number(vehicle.LugCount) && Math.abs(Number(bolt[2]) - Number(vehicle.BoltCircle)) < 0.2);
      }).map(variant => ({ product, variant })));
      const nativeResults=native?await Promise.all(choices.map(choice=>window.TWGFitmentPreview.evaluateSku(choice.variant.sku))):null;
      const rows = native?new Map():await service.wheels(choices.map(choice => choice.variant.sku));
      if (token !== alternativeToken || id !== (nativeKey(nativeVehicle()) || vehicleId())) return;
      const styles = new Set(); const direct = [];
      const evaluated = choices.map((choice,index) => ({ ...choice, result: nativeResults?nativeResults[index]:service.evaluate(vehicle, rows.get(String(choice.variant.sku).toUpperCase())) }))
        .filter(choice => choice.result.state === 'fits' || choice.result.state === 'caution')
        .sort((a, b) => Number(a.result.state === 'caution') - Number(b.result.state === 'caution'));
      for (const choice of evaluated) {
        const style = choice.product.style || choice.product.handle;
        const result = choice.result;
        if (styles.has(style)) continue;
        styles.add(style); direct.push({ ...choice, vehicle:native?null:vehicle, fitmentVehicle:native, result, ref: { handle: choice.product.handle, variant: String(choice.variant.id), vehicle: native?'':id, ...(native?{fitmentVehicle:native}:{}) } });
        if (direct.length === 3) break;
      }
      host.replaceChildren();
      if (!direct.length) host.append(element('p', L.alternatives_empty));
      else {
        const grid = element('div', null, 'mayhem-alternatives-grid');
        direct.forEach(setup => {
          const item = element('article'); const img = imageFor(setup); if (img) item.append(img);
          item.append(link(setup.product.title, productUrl(setup.ref)), element('p', `${setup.variant.sku} · ${window.MayhemFitmentUI.configuration(setup.variant)}`));
          const badge = element('div'); paintFitment(badge, setup); item.append(badge, button(L.compare, () => addCompare(setup.ref))); grid.append(item);
        }); host.append(grid);
      }
    } catch {
      if (token === alternativeToken) host.replaceChildren(element('p', L.alternatives_error), button(L.retry, alternatives));
    }
  };
  const garageKey = 'mayhem:garage:v1';
  let addingVehicle = false;
  const cleanVehicle = row => {
    if(row?.fitmentVehicle){const native=cleanNative(row.fitmentVehicle);return native?{id:nativeKey(native),fitmentVehicle:native}:null;}
    const id = String(row?.id || row?.Id || '');
    if (!/^\d{1,12}$/.test(id)) return null;
    const vehicle = { id };
    for (const key of ['year', 'make', 'model', 'submodel', 'body', 'bed']) vehicle[key] = String(row[key] ?? row[key[0].toUpperCase() + key.slice(1)] ?? '').slice(0,100);
    if (!vehicle.year || !vehicle.make || !vehicle.model) return null;
    return vehicle;
  };
  const garage = () => {
    const seen = new Set();
    return read(garageKey).map(cleanVehicle).filter(row => row && !seen.has(row.id) && seen.add(row.id)).slice(0,5);
  };
  const garageName = row => row.fitmentVehicle ? row.fitmentVehicle.label : [row.year, row.make, row.model, row.submodel, row.body, row.bed].filter(Boolean).join(' · ');
  const saveVehicle = row => {
    const vehicle = cleanVehicle(row);
    if (!vehicle) { announce(L.vehicle_unavailable); return false; }
    const values = garage();
    if (!values.some(item => item.id === vehicle.id) && values.length >= 5) { announce(L.garage_limit); return false; }
    const stored = persist(garageKey, [vehicle, ...values.filter(item => item.id !== vehicle.id)]);
    counters(); announce(stored ? L.garage_saved : L.garage_temporary);
    return true;
  };
  const startVehicle = event => {
    event?.stopPropagation();
    if (garage().length >= 5) { announce(L.garage_limit); return; }
    dialog.close();
    const anchor = [...document.querySelectorAll('[data-setup-action="garage"]')].find(node => node.getClientRects().length) || lastTrigger;
    addingVehicle = Boolean(window.mayhemOpenVehiclePicker?.(anchor));
    if (!addingVehicle) { announce(L.vehicle_unavailable); return; }
    document.querySelector('[data-mayhem-collection-vehicle-dialog]')?.addEventListener('close', () => {
      setTimeout(() => { addingVehicle = false; }, 0);
    }, { once:true });
  };
  const showGarage = () => {
    const token = open(L.garage, true);
    body.replaceChildren(element('p', L.garage_notice, 'mayhem-setup-small'));
    const savedTools = element('nav', null, 'mayhem-garage-tools');
    savedTools.setAttribute('aria-label', L.setup_title);
    savedTools.append(button(`${L.saved_setups} (${saved().length})`, () => showSaved()), button(`${L.compare} (${comparison().length})`, showCompare));
    body.append(savedTools);
    const values = garage();
    const activeNative=nativeVehicle();const activeId = nativeKey(activeNative) || vehicleId();
    const actions = element('div', null, 'mayhem-setup-actions');
    actions.append(button(L.garage_add, startVehicle));
    if (activeId && !values.some(item => item.id === activeId)) {
      const save = button(L.garage_save_current, async () => {
        save.disabled = true;
        try {
          const row = activeNative?{fitmentVehicle:activeNative}:await service.vehicle(activeId);
          if (token !== modalToken) return;
          if (saveVehicle(row)) showGarage();
        } catch { announce(L.vehicle_unavailable); }
        finally { save.disabled = false; }
      });
      actions.append(save);
    }
    body.append(actions);
    if (!values.length) body.append(element('p', L.garage_empty));
    const list = element('div', null, 'mayhem-saved-list mayhem-garage-list');
    for (const vehicle of values) {
      const item = element('article');
      item.append(element('h3', garageName(vehicle)));
      if (vehicle.id === activeId) item.append(element('p', L.garage_active, 'mayhem-garage-active'));
      const rowActions = element('div', null, 'mayhem-setup-actions');
      if (vehicle.id !== activeId) {
        const select = button(L.garage_select, async () => {
          select.disabled = true;
          try {
            if(vehicle.fitmentVehicle){await useVehicle({fitmentVehicle:vehicle.fitmentVehicle});if(dialog.open)showGarage();return;}
            const row = await service.vehicle(vehicle.id);
            if (token !== modalToken) return;
            await useVehicle({ vehicle:row });
            if (dialog.open && title.textContent === L.garage) showGarage();
          } catch { announce(L.vehicle_unavailable); }
          finally { select.disabled = false; }
        });
        rowActions.append(select);
      }
      rowActions.append(button(L.remove, () => {
        persist(garageKey, garage().filter(row => row.id !== vehicle.id)); counters(); showGarage(); announce(L.garage_removed);
      }));
      const setupCount = saved().filter(ref => (nativeKey(cleanNative(ref.fitmentVehicle)) || ref.vehicle) === vehicle.id).length;
      if (setupCount) rowActions.append(button(`${L.saved_setups} (${setupCount})`, () => showSaved(vehicle.id)));
      item.append(rowActions); list.append(item);
    }
    body.append(list);
  };

  document.addEventListener('click', event => {
    const control = event.target.closest('[data-setup-action]'); if (!control) return;
    const action = control.dataset.setupAction;
    if (action === 'close') { dialog.close(); return; }
    if (['garage', 'saved', 'compare'].includes(action)) {
      const heading = action === 'garage' ? L.garage : action === 'saved' ? L.saved_setups : L.compare;
      if (dialog.open && lastTrigger === control && title.textContent === heading) { dialog.close(); return; }
      lastTrigger?.setAttribute('aria-expanded', 'false');
      lastTrigger = control;
    } else if (!dialog.open) lastTrigger = control;
    if (action === 'garage') {
      const menu = control.closest('header-drawer')?.querySelector('details');
      if (menu?.open) menu.querySelector('summary')?.click();
      showGarage(); return;
    }
    if (action === 'saved') { showSaved(); return; }
    if (action === 'compare') { showCompare(); return; }
    if (action === 'compare-card') { event.preventDefault(); chooseCard(control.closest('[data-mayhem-wheel-card]')); return; }
    const ref = currentRef(); if (!ref) { announce(L.select_configuration); return; }
    if (action === 'save-current') saveSetup(ref);
    if (action === 'share-current') showSetup(ref, true);
    if (action === 'view-current') showSetup(ref);
    if (action === 'compare-current') addCompare(ref);
  });
  dialog.addEventListener('close', () => {
    if (dialog.open) return;
    ++modalToken; lastTrigger?.setAttribute('aria-expanded', 'false');
    const feedback = dialog.querySelector('[data-setup-toast]');
    if (feedback) { feedback.hidden = true; document.body.append(feedback); }
    if (!document.querySelector('dialog[open]') && lastTrigger?.isConnected && lastTrigger.getClientRects().length) lastTrigger.focus();
  });
  document.addEventListener('click', event => {
    if (dialog.open && dialog.classList.contains('mayhem-setup-menu') && !event.composedPath().includes(dialog) && !event.target.closest('[data-setup-action]') && !lastTrigger?.contains(event.target)) dialog.close();
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && dialog.open && !dialog.matches(':modal')) { event.preventDefault(); dialog.close(); }
  });
  window.addEventListener('resize', () => { if (dialog.open && dialog.classList.contains('mayhem-setup-menu')) present(); });
  document.addEventListener('scroll', event => {
    if (dialog.open && dialog.classList.contains('mayhem-setup-menu') && !dialog.contains(event.target)) positionMenu();
  }, true);
  dialog.addEventListener('click', event => { if (event.target === dialog) { const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close(); } });
  document.addEventListener('mayhem:spec-selection-changed', syncCurrent);
  window.addEventListener('twg:vehicle-changed',()=>{
    const requestedSetup = setupVehicleRequest;
    setupVehicleRequest = null;
    if (requestedSetup && nativeVehicle()) showSetup({ ...requestedSetup, vehicle: '', fitmentVehicle: nativeVehicle() });
    ++alternativeToken;counters();syncCurrent();alternatives();
    if(addingVehicle && nativeVehicle()){addingVehicle=false;saveVehicle({fitmentVehicle:nativeVehicle()});}
    if(dialog.open && title.textContent===L.garage)showGarage();
    if(dialog.open && title.textContent===L.compare){if(dialog.classList.contains('mayhem-setup-menu'))showCompare();else showComparisonTable();}
  });
  window.addEventListener('mayhem:vehicle-changed', () => { ++alternativeToken; syncCurrent(); alternatives(); if (dialog.open && title.textContent === L.compare) { if (dialog.classList.contains('mayhem-setup-menu')) showCompare(); else showComparisonTable(); } });
  window.addEventListener('mayhem:vehicle-changed', event => {
    if (addingVehicle && event.detail?.id) {
      addingVehicle = false;
      saveVehicle({ ...event.detail.vehicle, id:String(event.detail.id) });
    }
    if (dialog.open && title.textContent === L.garage) showGarage();
  });
  window.addEventListener('storage', () => { memory.clear(); counters(); syncCurrent(); ++alternativeToken; alternatives(); if (dialog.open && title.textContent === L.garage) showGarage(); });
  window.addEventListener('pageshow', () => { counters(); ++alternativeToken; alternatives(); });
  const alternativeHost = document.querySelector('[data-mayhem-alternatives]');
  if (alternativeHost) {
    const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { alternativesVisible = true; observer.disconnect(); alternatives(); } }, { rootMargin: '300px' }); observer.observe(alternativeHost);
  }
  counters(); syncCurrent(); initHandoff();
  if (requestedVariant) {
    const data = document.querySelector('[data-mayhem-setup-product]');
    try {
      const product = data && JSON.parse(data.textContent);
      if (product && !product.variants.some(row => String(row.id) === requestedVariant)) {
        const notice = element('aside', null, 'mayhem-handoff');
        notice.setAttribute('role', 'status');
        notice.append(element('p', L.configuration_unavailable), link(L.browse_wheels, preview(new URL('/collections/all-wheels', location.origin)).href));
        const host = document.querySelector('[data-mayhem-pdp-summary]');
        if (host) host.before(notice);
      }
    } catch {}
  }
  const incoming = refFromUrl(); if (incoming && document.querySelector('[data-mayhem-pdp-summary]')) showSetup(incoming);
  window.MayhemSetupTools = { currentRef, setupUrl, validRef, saveSetup, showSetup };
})();










